import React from "react";
// @ts-ignore
import ProImage from "../../assets/pro.png";

const PAYMENT_LINK = "https://rzp.io/rzp/bhPznAT";

const ZaffloPremiumCard = ({ userDetails }) => {

    const isTrial = userDetails?.accountType === "TRIAL";

    // Unified expiry field
    const expiryDateStr =
        userDetails?.validTill ||
        userDetails?.nextPaymentDate ||
        userDetails?.trialValidTill;

    const handleUpgrade = () => {
        window.open(PAYMENT_LINK, "_blank");
    };

    // ---------- DATE CALCULATIONS ----------
    let expiry = null;
    let diffDays = null;

    if (expiryDateStr) {
        expiry = new Date(expiryDateStr + "T00:00:00");
        const today = new Date();
        diffDays = Math.ceil(
            (expiry - today) / (1000 * 60 * 60 * 24)
        );
    }

    // ---------- CARD HIGHLIGHT ----------
    const highlightTrial = isTrial && diffDays !== null && diffDays <= 3;

    // UI REFINEMENT: Ultra-thin borders and deep ambient shadows
    const cardClasses = `
        relative isolate overflow-hidden
        bg-white p-10 rounded-[2.5rem]
        transition-all duration-700 ease-out
        border w-full h-full flex flex-col justify-center
        ${
            highlightTrial
                ? "shadow-[0_40px_80px_-15px_rgba(255,120,0,0.2)] border-orange-200"
                : "shadow-[0_30px_70px_-20px_rgba(0,0,0,0.1)] hover:shadow-[0_40px_90px_-15px_rgba(108,187,35,0.2)] border-slate-100/80"
        }
    `;

    // ---------- VALIDITY UI ----------
    const renderValidity = () => {
        if (!expiry || diffDays === null) return null;

        let colorClass = "text-[#6cbb23]";
        let bgClass = "bg-[#f1fbe9]";
        if (diffDays <= 14) { colorClass = "text-orange-600"; bgClass = "bg-orange-50"; }
        if (diffDays <= 7) { colorClass = "text-red-600"; bgClass = "bg-red-50"; }

        if (diffDays < 0) {
            return (
                <div className="flex justify-center mt-8">
                    <span className="bg-slate-900 text-white text-[10px] font-bold tracking-[0.2em] px-4 py-1.5 rounded-full uppercase">
                        {isTrial ? "Trial Ended" : "Access Expired"}
                    </span>
                </div>
            );
        }

        return (
            <div className={`mt-8 py-3 px-4 rounded-2xl ${bgClass} border border-current opacity-2 flex flex-col items-center justify-center`}>
                <span className={`text-[10px] font-black uppercase tracking-widest ${colorClass}`}>
                    {isTrial ? "Trial Period" : "Membership Active"}
                </span>
                <p className={`text-sm font-bold mt-1 ${colorClass}`}>
                    {isTrial ? `${diffDays} Days Left` : `Valid until ${expiry.toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' })}`}
                </p>
            </div>
        );
    };

    // ---------- CTA LABEL ----------
    const getCtaLabel = () => {
        if (!isTrial) return "Extend Membership";
        if (diffDays !== null && diffDays <= 3) return "Claim Pro Access";
        return "Upgrade to Zafflo Pro";
    };

    return (
        <div className="w-full h-full">
            <div className={cardClasses + " h-full flex flex-col"}>
                
                {/* Decorative Blur - Branding */}
                <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6cbb23]/10 blur-[100px] -z-10 rounded-full" />
                <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#f1fbe9] blur-[100px] -z-10 rounded-full" />

                {isTrial ? (
                    <div className="flex flex-col justify-center h-full relative z-10">

                        {/* HEADER */}
                        <div className="text-center mb-10">
                            <div className="inline-flex items-center gap-2 mb-4">
                                <span className="h-[1px] w-8 bg-slate-200"></span>
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Premium Experience</span>
                                <span className="h-[1px] w-8 bg-slate-200"></span>
                            </div>

                            <h3 className="text-4xl font-black text-slate-900 tracking-tight leading-none mb-3">
                                Elevate <span className="text-[#6cbb23]">Everything.</span>
                            </h3>

                            <p className="text-slate-400 text-base font-medium max-w-[280px] mx-auto">
                                Join the elite tier of businesses using Zafflo Pro.
                            </p>
                        </div>

                        {/* PRICE BOX - Sleek & Modern */}
                        <div className="relative group mb-8">
                            <div className="absolute inset-0 bg-[#6cbb23]/5 rounded-[2.5rem] scale-105 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                            
                            <div className="relative bg-slate-50 border border-slate-100 rounded-[2.5rem] p-10 text-center transition-transform duration-500 hover:scale-[1.02]">
                                <div className="flex items-start justify-center gap-1">
                                    <span className="text-xl font-bold text-slate-400 mt-2">₹</span>
                                    <span className="text-6xl font-[1000] text-slate-900 tracking-tighter tabular-nums">399*</span>
                                </div>
                                
                                <div className="mt-4 inline-flex items-center gap-2 bg-white px-4 py-1.5 rounded-full border border-slate-100 shadow-sm">
                                    <span className="w-2 h-2 rounded-full bg-[#6cbb23]"></span>
                                    <span className="text-[11px] font-bold text-slate-600 uppercase tracking-tight">Best Value</span>
                                </div>
                                
                                <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mt-6">
                                    Approximately ₹4,788* per year
                                </p>
                            </div>
                        </div>

                        {/* ACTION BUTTON */}
                        <button
                            onClick={handleUpgrade}
                            className="
                                group relative
                                w-full
                                bg-slate-900
                                text-white font-bold
                                py-5 rounded-[1.5rem] text-lg
                                shadow-2xl shadow-slate-200
                                hover:bg-[#6cbb23]
                                hover:shadow-[#6cbb23]/30
                                active:scale-95
                                transition-all duration-300
                            "
                        >
                            <span className="relative z-10 flex items-center justify-center gap-2">
                                {getCtaLabel()}
                                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 7l5 5m0 0l-5 5m5-5H6"></path></svg>
                            </span>
                        </button>

                        <div className="flex flex-col items-center gap-1 mt-6">
                            <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">Powered by Razorpay Secure</p>
                            {renderValidity()}
                        </div>

                    </div>
                ) : (
                    <div className="flex flex-col justify-center h-full text-center">
                            
                        {/* PRO ACTIVE HEADER */}
                        <div className="mb-6">
                            <div className="w-20 h-20 bg-[#f1fbe9] rounded-3xl mx-auto flex items-center justify-center mb-6 rotate-3 hover:rotate-0 transition-transform duration-500">
                                <svg className="w-10 h-10 text-[#6cbb23]" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg>
                            </div>
                            <h3 className="text-3xl font-black text-slate-900 tracking-tight uppercase">Zafflo <span className="text-[#6cbb23]">Pro</span></h3>
                        </div>

                        <div className="relative group">
                            <div className="absolute inset-0 bg-[#6cbb23]/20 blur-[60px] rounded-full scale-50 group-hover:scale-90 transition-transform duration-1000"></div>
                            <img
                                src={ProImage}
                                alt="Pro User"
                                className="relative z-10 mx-auto max-h-72 object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.1)]"
                            />
                        </div>

                        <div className="bg-slate-50 p-6 rounded-[2rem] border border-slate-100 mt-4">
                            <p className="text-slate-500 text-sm font-bold uppercase tracking-widest leading-relaxed">
                                Elite Performance Active
                            </p>
                        </div>

                        {renderValidity()}

                    </div>
                )}

            </div>
        </div>
    );
};

export default ZaffloPremiumCard;